import { PlatformSiteAri } from '@atlassian/ari';
import { captureException } from '@sentry/react';

import { resolvedActivityProvider } from '@townsquare/activity-provider/provider';
import { getWorkedOnAndViewedItemsResults } from '@townsquare/activity-provider/types';
import { resolvedAvailableSitesProvider } from '@townsquare/available-sites-provider/provider';
import type { getAvailableProductsResult } from '@townsquare/available-sites-provider/types';
import { resolvedProductConfigurationProvider } from '@townsquare/product-configuration-provider';
import type { getProductConfigurationResult } from '@townsquare/product-configuration-provider/types';
import { createResource } from '@townsquare/router/resources/createResource';
import type { RouteResource } from '@townsquare/router/resources/types';
import type { HomeResourceContext } from '@townsquare/router/types';

import { UNTENANTED_PRODUCTS } from './consts';

export interface AppsResourceData {
	products: getAvailableProductsResult;
	config: getProductConfigurationResult;
	activity: getWorkedOnAndViewedItemsResults;
}

const emptyActivityResult = (): getWorkedOnAndViewedItemsResults => ({
	data: { viewed: [], workedOn: [] },
	error: false,
});

const getSafeWorkedOnAndViewedItems = async (
	cloudIds: string[],
	collabContextId: PlatformSiteAri,
): Promise<getWorkedOnAndViewedItemsResults> => {
	if (!resolvedActivityProvider) {
		return emptyActivityResult();
	}

	return resolvedActivityProvider
		.getWorkedOnAndViewedItemsWithPostFiltering({
			cloudIds: cloudIds.concat(UNTENANTED_PRODUCTS),
			collabContextId,
		})
		.catch((error) => {
			captureException(error);
			return emptyActivityResult();
		});
};

export const appsResource: RouteResource<AppsResourceData> = createResource<AppsResourceData>({
	getData: async (_, resourceContext) => {
		const context = resourceContext as HomeResourceContext;

		const orgId = context.orgId ?? '';
		const cloudIds = context.collabContext?.orgCloudIds ?? [];
		const rovoWorkspace = context.collabContext?.rovoWorkspace;

		const [products, config, activity] = await Promise.all([
			resolvedAvailableSitesProvider.getAvailableProducts({ cloudIdsToFilterFor: cloudIds, orgId }),
			resolvedProductConfigurationProvider.getProductConfiguration(),
			rovoWorkspace
				? getSafeWorkedOnAndViewedItems(cloudIds, PlatformSiteAri.create({ siteId: rovoWorkspace }))
				: emptyActivityResult(),
		]);

		return {
			products,
			config,
			activity,
		};
	},
	getKey: (_, resourceContext) => {
		const context = resourceContext as HomeResourceContext;

		const orgId = context.orgId ?? '';

		return `apps-resource-key-${orgId}`;
	},
	type: 'APP_RESOURCE',
	maxAge: 60_000 * 30, // 30 minute cache during the SPA session
});
